import { set, unescape } from 'lodash'
import { Schema } from '@wix/cms-viewer-core'
import { FieldType } from '@wix/wix-data-schema-types'

import { adaptLegacyConnection } from './legacy-connection-adapter'

export const makeLegacyComponentAdapter = ({
  schema, schemas, features, mediaUtils,
}) => (legacyComponent) => {
  const accessibilityHack = initAccessibilityHack(legacyComponent, features)
  const component = {
    id: legacyComponent.uniqueId,
    connection: adaptLegacyConnection(legacyComponent.connectionConfig, legacyComponent.role, schema),
    setProps: (props) => {
      const propErrors = setPropsToLegacyComponent(legacyComponent, props, schema, schemas, mediaUtils)

      if (propErrors.length > 0) {
        const { errors, props } = propErrors.reduce((acc, [prop, error]) => ({
          errors: [...acc.errors, error],
          props: [...acc.props, prop],
        }), { errors: [], props: [] })
        throw new AggregateError(errors, `Failed to set properties: ${props.join(', ')}`)
      }
    },
    bind: (events) => {
      for (const [event, handler] of Object.entries(events)) {
        legacyComponent[event](handler)
      }
    },
    execute: (command, ...args) => {
      legacyComponent[command](...args)
      applyAccessibilityHack(accessibilityHack, legacyComponent, command)
    },
    getProp: (propName) => {
      if (propName === 'disabled') {
        return !legacyComponent.enabled
      }
      return legacyComponent[propName]
    },
  }

  return component
}

const setPropsToLegacyComponent = (legacyComponent, props, schema, schemas, mediaUtils) => {
  const propErrors = []
  const { htmlLinkProps, disabledProps, linkProps, restProps } = adaptProps(props, legacyComponent, schema, schemas)

  if (htmlLinkProps) {
    trySettingProp(
      () => setHtmlLinkPreservingFormatting(legacyComponent, htmlLinkProps),
      '$text (connected to "url" field)',
      propErrors,
    )
  }

  if (disabledProps) {
    trySettingProp(
      () => setDisabledState(legacyComponent, disabledProps),
      'disabled (disable/enable component method)',
      propErrors,
    )
  }

  if (linkProps) {
    const { link } = linkProps
    trySettingProp(
      () => {
        legacyComponent.link = link ? tryConvertingMediaUrl(link, mediaUtils) : ''
      },
      'link',
      propErrors,
    )
  }

  for (const [prop, value] of Object.entries(restProps)) {
    trySettingProp(() => set(legacyComponent, prop, value), prop, propErrors)
  }

  return propErrors
}

const tryConvertingMediaUrl = (value, mediaUtils) => {
  try {
    return mediaUtils.convertToUrl(value)
  } catch {
    return value
  }
}

const trySettingProp = (setProp, propDescription, errors) => {
  try {
    setProp()
  } catch (error) {
    errors.push([propDescription, error])
  }
}

const adaptProps = (props, legacyComponent, schema, schemas) => {
  const { $text, disabled, link, ...restProps } = props
  const adaptedProps = { restProps }

  if (disabled !== undefined) {
    adaptedProps.disabledProps = { disabled }
  }

  if ($text !== undefined) {
    const { connectionConfig: { properties: { $text: { fieldName } } } } = legacyComponent
    const [fieldKey, referencedFieldKey] = fieldName.split('.')
    const resolvedFieldType = referencedFieldKey
      ? Schema.getReferencedFieldType(schema, Object.values(schemas), { fieldKey, referencedFieldKey })
      : Schema.getFieldType(schema, fieldKey)

    if (resolvedFieldType === FieldType.url) {
      const { target, rel, ...rest } = restProps
      adaptedProps.htmlLinkProps = { href: $text, target, rel }
      adaptedProps.restProps = rest
    } else {
      const componentProp = resolvedFieldType === FieldType.richText ? 'html' : 'text'
      adaptedProps.restProps = { ...restProps, [componentProp]: $text }
    }
  } else if ('link' in props) {
    adaptedProps.linkProps = { link }
  }

  return adaptedProps
}

const setHtmlLinkPreservingFormatting = (legacyComponent, { href, target, rel }) => {
  // yes, this is a shitty hack
  const link = `<a href=${href} target="${target ?? '_blank'}"${rel ? ` rel="${rel}"` : ''} style="text-decoration: underline">${href}</a>`
  legacyComponent.text = link
  const { html } = legacyComponent
  legacyComponent.text = ''
  legacyComponent.html = unescape(html)
}

const setDisabledState = (legacyComponent, { disabled }) => {
  if (legacyComponent.enabled && disabled) {
    legacyComponent.disable()
  } else if (!legacyComponent.enabled && !disabled) {
    legacyComponent.enable()
  }
}

const initAccessibilityHack = (legacyComponent, features) => {
  if (features.accessibilityBindings && legacyComponent.connectionConfig.behaviors?.length > 0) {
    const originalHtml = legacyComponent.html
    const originalHtmlHiddenFromAccessibilityTree = `<div aria-hidden="true">${originalHtml}</div>`
    legacyComponent.html = originalHtmlHiddenFromAccessibilityTree
    legacyComponent.accessibility.ariaLive = 'polite'
    const announcement = { state: { type: 'untriggered' } }
    return {
      originalHtml,
      originalHtmlHiddenFromAccessibilityTree,
      announcement,
    }
  }
  return undefined
}

const applyAccessibilityHack = (accessibilityHack, legacyComponent, command) => {
  if (accessibilityHack) {
    const { originalHtml, originalHtmlHiddenFromAccessibilityTree, announcement } = accessibilityHack
    if (command === 'show') {
      if (announcement.state.type === 'untriggered') {
        const timerId = setTimeout(() => {
          legacyComponent.html = `${originalHtmlHiddenFromAccessibilityTree}<div style="height:0;width:0;overflow:hidden">${originalHtml}</div>`
          announcement.state = { type: 'triggered' }
        }, 150)
        announcement.state = { type: 'pendingTriggering', timerId }
      }
    } else if (command === 'hide') {
      if (announcement.state.type === 'pendingTriggering') {
        clearTimeout(announcement.state.timerId)
        announcement.state = { type: 'untriggered' }
      } else {
        legacyComponent.html = originalHtmlHiddenFromAccessibilityTree
      }
    }
  }
}
